import { useEffect, useRef } from 'react'

// Hook de tracking GA4 para artículos del blog. Eventos emitidos:
//   - blog_view          (al montar)
//   - blog_scroll        (al cruzar 25/50/75/100% de la página, una sola vez cada uno)
//   - blog_time          (al desmontar; envía segundos en página, redondeados)
//   - blog_read_complete (cuando alcanza el 90% de scroll, una única vez)
// El envío se omite silenciosamente si window.gtag no existe (Consent Mode v2 no
// concedido o entornos sin GA cargado).

const sendEvent = (name, params) => {
  if (typeof window === 'undefined') return
  if (typeof window.gtag !== 'function') return
  window.gtag('event', name, params)
}

export default function useBlogTracking({ slug, category, readMinutes } = {}) {
  const startedAt = useRef(Date.now())
  const milestonesHit = useRef(new Set())
  const completed = useRef(false)

  useEffect(() => {
    if (!slug) return undefined

    sendEvent('blog_view', { slug, categoria: category || 'sin_categoria', read_minutes: readMinutes || 0 })

    const onScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop
      const docHeight = document.documentElement.scrollHeight - window.innerHeight
      if (docHeight <= 0) return
      const pct = Math.min(100, Math.round((scrollTop / docHeight) * 100))

      ;[25, 50, 75, 100].forEach((mark) => {
        if (pct >= mark && !milestonesHit.current.has(mark)) {
          milestonesHit.current.add(mark)
          sendEvent('blog_scroll', { slug, depth_pct: mark })
        }
      })

      if (pct >= 90 && !completed.current) {
        completed.current = true
        const seconds = Math.round((Date.now() - startedAt.current) / 1000)
        sendEvent('blog_read_complete', { slug, segundos: seconds })
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()

    return () => {
      window.removeEventListener('scroll', onScroll)
      const seconds = Math.round((Date.now() - startedAt.current) / 1000)
      // Solo emitimos blog_time si pasó al menos 5 segundos para descartar rebotes accidentales.
      if (seconds >= 5) sendEvent('blog_time', { slug, segundos: seconds })
    }
  }, [slug, category, readMinutes])
}

// Helper para CTAs dentro de posts (newsletter, registro, enlaces internos, etc.).
// Pásale el slug del post y la ubicación lógica del CTA.
export const trackBlogCta = (slug, ubicacion, extra = {}) => {
  sendEvent('blog_cta_click', { slug, ubicacion, ...extra })
}
